import { useState, type CSSProperties } from "react";
import { cn } from "../lib/utils";

export type CardVariant = "visa" | "mastercard" | "amex" | "generic";

interface CreditCardDisplayProps {
  number?: string;
  name?: string;
  expiry?: string;
  cvc?: string;
  flipped?: boolean;
  variant?: CardVariant;
  className?: string;
  style?: CSSProperties;
}

function mask(num: string) {
  const clean = num.replace(/\D/g, "").padEnd(16, "•");
  return [clean.slice(0,4), clean.slice(4,8), clean.slice(8,12), clean.slice(12,16)].join("  ");
}

function NetworkLogo({ variant }: { variant: CardVariant }) {
  if (variant === "visa") return (
    <span className="cc-network cc-visa">VISA</span>
  );
  if (variant === "mastercard") return (
    <span className="cc-network cc-mc">
      <span className="cc-mc-c cc-mc-c1" />
      <span className="cc-mc-c cc-mc-c2" />
    </span>
  );
  return <span className="cc-network cc-generic">PAY</span>;
}

export function CreditCardDisplay({
  number = "", name = "CARDHOLDER NAME", expiry = "MM/YY",
  cvc = "•••", flipped = false, variant = "generic", className, style,
}: CreditCardDisplayProps) {
  return (
    <div className={cn("cc-scene", className)} style={style}>
      <div className={cn("cc-card", flipped && "cc-card-flipped")}>
        {/* Front */}
        <div className="cc-face cc-front">
          <div className="cc-shine" aria-hidden="true" />
          <NetworkLogo variant={variant} />
          <div className="cc-chip" aria-hidden="true">
            <div className="cc-chip-line" /><div className="cc-chip-line" />
          </div>
          <div className="cc-number">{mask(number)}</div>
          <div className="cc-row">
            <div>
              <div className="cc-field-label">Card holder</div>
              <div className="cc-field-value">{name || "CARDHOLDER NAME"}</div>
            </div>
            <div>
              <div className="cc-field-label">Expires</div>
              <div className="cc-field-value">{expiry || "MM/YY"}</div>
            </div>
          </div>
        </div>
        {/* Back */}
        <div className="cc-face cc-back">
          <div className="cc-stripe" aria-hidden="true" />
          <div className="cc-cvc-row">
            <div className="cc-cvc-label">CVV</div>
            <div className="cc-cvc-box">{cvc}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

interface CreditCardFormProps {
  className?: string;
}

export function CreditCardForm({ className }: CreditCardFormProps) {
  const [num, setNum] = useState("");
  const [name, setName] = useState("");
  const [expiry, setExpiry] = useState("");
  const [cvc, setCvc] = useState("");
  const [flipped, setFlipped] = useState(false);

  function fmtNum(v: string) {
    return v.replace(/\D/g,"").slice(0,16).replace(/(.{4})/g,"$1 ").trim();
  }
  function fmtExpiry(v: string) {
    const d = v.replace(/\D/g,"").slice(0,4);
    return d.length > 2 ? `${d.slice(0,2)}/${d.slice(2)}` : d;
  }

  return (
    <div className={cn("cc-form", className)}>
      <CreditCardDisplay number={num} name={name.toUpperCase()} expiry={expiry} cvc={cvc} flipped={flipped} />
      <div className="cc-fields">
        <div className="cc-field">
          <label className="cc-label">Card number</label>
          <input className="cc-input" value={num} onChange={e => setNum(fmtNum(e.target.value))} placeholder="1234 5678 9012 3456" maxLength={19} />
        </div>
        <div className="cc-field">
          <label className="cc-label">Cardholder name</label>
          <input className="cc-input" value={name} onChange={e => setName(e.target.value)} placeholder="John Doe" />
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem" }}>
          <div className="cc-field">
            <label className="cc-label">Expiry</label>
            <input className="cc-input" value={expiry} onChange={e => setExpiry(fmtExpiry(e.target.value))} placeholder="MM/YY" maxLength={5} />
          </div>
          <div className="cc-field">
            <label className="cc-label">CVV</label>
            <input className="cc-input" value={cvc} onChange={e => setCvc(e.target.value.replace(/\D/g,"").slice(0,4))} placeholder="•••" onFocus={() => setFlipped(true)} onBlur={() => setFlipped(false)} maxLength={4} />
          </div>
        </div>
      </div>
    </div>
  );
}
